import apiService from './api.service';

/** Backend wraps payloads in `{ success, message, data }` (ResponseUtil.success). */
function unwrap<T>(body: unknown): T {
  if (body != null && typeof body === 'object' && 'data' in body && (body as { success?: boolean }).success !== false) {
    const inner = (body as { data?: T }).data;
    if (inner !== undefined) return inner;
  }
  return body as T;
}

// Dashboard API endpoints
export const dashboardService = {
  async getStatistics() {
    const body = await apiService.get('/dashboard/statistics');
    return unwrap<DashboardStatistics & { currency?: string; currencySymbol?: string }>(body);
  },

  async getRevenueTrend(params?: { startDate?: string; endDate?: string }) {
    const body = await apiService.get('/dashboard/revenue-trend', { params });
    return unwrap<RevenueDataPoint[]>(body);
  },

  async getSalesByType(params?: { startDate?: string; endDate?: string }) {
    const body = await apiService.get('/dashboard/sales-by-type', { params });
    return unwrap<SalesTypeData[]>(body);
  },

  async getRecentActivities(limit: number = 10) {
    const body = await apiService.get('/dashboard/activities', {
      params: { limit },
    });
    return unwrap<Activity[]>(body);
  },

  async getPendingApprovals() {
    const body = await apiService.get('/dashboard/pending-approvals');
    return unwrap<PendingApproval[]>(body);
  },

  async getProjectStatus() {
    const body = await apiService.get('/dashboard/project-status');
    return unwrap<ProjectStatus[]>(body);
  },

  async getTopCustomers(limit: number = 5) {
    const body = await apiService.get('/dashboard/top-customers', {
      params: { limit },
    });
    return unwrap<TopCustomerRow[]>(body);
  },
};

// TypeScript interfaces for dashboard data
export interface DashboardStatistics {
  totalRevenue: number;
  revenueTrend: number;
  salesOrders: number;
  ordersTrend: number;
  activeProjects: number;
  projectsTrend: number;
  profitMargin: number;
  profitTrend: number;
  currency?: string;
  currencySymbol?: string;
}

export interface TopCustomerRow {
  name: string;
  value: number;
  valueFormatted?: string;
  projects?: number;
  status?: string;
}

export interface RevenueDataPoint {
  month: string;
  revenue: number;
  profit: number;
}

export interface SalesTypeData {
  name: string;
  value: number;
  color: string;
}

export interface Activity {
  id: string;
  action: string;
  description: string;
  time: string;
  type: 'sale' | 'invoice' | 'project' | 'purchase' | 'other';
}

export interface PendingApproval {
  type: string;
  count: number;
  priority: 'high' | 'medium' | 'low';
}

export interface ProjectStatus {
  status: string;
  count: number;
  color: string;
}

